/**
 * The i18n context and its hook. The DEFAULT value is a working English translator, so a
 * component rendered outside `<I18nProvider>` (a unit test, a harness) still renders English
 * rather than crashing or printing keys.
 */
import { Fragment, createContext, createElement, useContext, type ReactNode } from "react";
import { DEFAULT_LOCALE, getEnabledLocales, type AppLocale, type LocaleCode, type LocaleDefinition } from "@shared/i18n/localeConfig";
import type { LocaleSource } from "@shared/i18n/localePreference";
import { EN_CATALOG, type MessageKey } from "./englishCatalog";
import { createFormatters, type Formatters } from "./format";
import { Translator, type MessageValues, type RichMessageValues } from "./translator";

export interface I18nContextValue {
  /** The locale on screen right now. */
  readonly locale: AppLocale;
  /** Why that locale was chosen. */
  readonly source: LocaleSource;
  /** The device's valid explicit choice, or null when the player has not chosen (automatic). */
  readonly explicitLocale: AppLocale | null;
  /** What automatic detection alone would pick (shown on the "Automatic" option). */
  readonly automaticLocale: LocaleCode;
  /** True while a newly chosen locale's catalog is still downloading (the old one stays up). */
  readonly isLoading: boolean;
  /** The languages the selector offers. */
  readonly selectableLocales: readonly LocaleDefinition[];
  readonly pseudoAvailable: boolean;
  readonly t: (key: MessageKey, values?: MessageValues) => string;
  readonly rich: (key: MessageKey, values: RichMessageValues<ReactNode>) => ReactNode;
  readonly format: Formatters;
  /** An explicit choice: stored on this device, never overwritten by detection. */
  readonly setLocale: (locale: AppLocale) => void;
  /** Back to automatic detection: forgets the explicit choice. */
  readonly clearLocaleChoice: () => void;
}

/** ICU returns rich text as an array of chunks; give each a key so React can reconcile them. */
export function keyChunks(out: ReactNode | ReactNode[]): ReactNode {
  return Array.isArray(out) ? out.map((chunk, i) => createElement(Fragment, { key: i }, chunk)) : out;
}

const englishTranslator = new Translator({ locale: DEFAULT_LOCALE, messages: EN_CATALOG, fallback: EN_CATALOG });

export const ENGLISH_I18N: I18nContextValue = Object.freeze<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  source: "default",
  explicitLocale: null,
  automaticLocale: DEFAULT_LOCALE,
  isLoading: false,
  selectableLocales: getEnabledLocales(),
  pseudoAvailable: false,
  t: (key, values) => englishTranslator.t(key, values),
  rich: (key, values) => keyChunks(englishTranslator.rich<ReactNode>(key, values)),
  format: createFormatters(DEFAULT_LOCALE),
  setLocale: () => undefined,
  clearLocaleChoice: () => undefined,
});

export const I18nContext = createContext<I18nContextValue>(ENGLISH_I18N);

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Shorthand for the common case: just the `t` function. */
export function useT(): I18nContextValue["t"] {
  return useContext(I18nContext).t;
}
